import React, { useEffect, useState } from 'react';
import { api, errText } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { WaHoursSettings } from './WaHoursSettings';

const Row = ({ title, desc, children }) => (
  <div className="flex items-center justify-between gap-6 py-4 border-b border-border last:border-0">
    <div><div className="font-medium text-sm">{title}</div><div className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5">{desc}</div></div>
    {children}
  </div>
);

export const AiSettings = ({ settings, isAdmin, onSaved }) => {
  const [form, setForm] = useState(settings);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setForm(settings); }, [settings]);
  if (!form) return null;

  const save = async () => {
    setBusy(true);
    try { const { data } = await api.put('/ai/settings', form); onSaved(data); toast.success('Ajustes del agente guardados'); }
    catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };

  return (
    <Card className="p-5 max-w-3xl">
      <Row title="Análisis automático de tickets nuevos" desc="Al llegar ticket.created por webhook, el agente lo analiza y prepara una respuesta.">
        <Switch checked={form.auto_enabled} disabled={!isAdmin} onCheckedChange={(v) => setForm((f) => ({ ...f, auto_enabled: v }))} data-testid="ai-set-auto" />
      </Row>
      <Row title="Responder solo los tickets sencillos" desc="Si el ticket es sencillo y supera el umbral de confianza, se responde al pastor sin intervención. El resto queda como borrador.">
        <Switch checked={form.auto_reply_enabled} disabled={!isAdmin || !form.auto_enabled} onCheckedChange={(v) => setForm((f) => ({ ...f, auto_reply_enabled: v }))} data-testid="ai-set-autoreply" />
      </Row>
      <Row title={`Umbral de confianza: ${Math.round(form.confidence_threshold * 100)}%`} desc="Mientras más alto, más conservador: menos respuestas automáticas.">
        <Slider className="w-56" min={50} max={100} step={5} disabled={!isAdmin} value={[Math.round(form.confidence_threshold * 100)]} onValueChange={([v]) => setForm((f) => ({ ...f, confidence_threshold: v / 100 }))} data-testid="ai-set-threshold" />
      </Row>
      <Row title="Minutos ahorrados por ticket" desc="Se usan para calcular el tiempo ahorrado en Métricas.">
        <div className="flex items-center gap-3 text-xs">
          <label className="flex items-center gap-1.5">Auto
            <Input type="number" min={0} max={240} className="w-20" disabled={!isAdmin} value={form.minutes_auto ?? 10} onChange={(e) => setForm((f) => ({ ...f, minutes_auto: Number(e.target.value) }))} data-testid="ai-set-min-auto" />
          </label>
          <label className="flex items-center gap-1.5">Borrador
            <Input type="number" min={0} max={240} className="w-20" disabled={!isAdmin} value={form.minutes_draft ?? 5} onChange={(e) => setForm((f) => ({ ...f, minutes_draft: Number(e.target.value) }))} data-testid="ai-set-min-draft" />
          </label>
        </div>
      </Row>
      <WaHoursSettings value={form.wa_hours} disabled={!isAdmin} onChange={(wa_hours) => setForm((f) => ({ ...f, wa_hours }))} />
      <div className="flex items-center justify-between pt-4">
        <span className="text-xs text-[hsl(var(--muted-foreground))]">Modelo: Claude Sonnet 4.5</span>
        <div className="flex items-center gap-3">
          {!isAdmin && <span className="text-xs text-[hsl(var(--muted-foreground))]">Solo un admin puede cambiar estos ajustes</span>}
          <Button onClick={save} disabled={busy || !isAdmin} data-testid="ai-set-save">Guardar ajustes</Button>
        </div>
      </div>
    </Card>
  );
};
